import { db, auth } from "../firebase";
import { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  getCountFromServer,
  deleteDoc,
  updateDoc,
  getDoc,
  doc,
  query,
  where,
  limit,
  startAfter,
  serverTimestamp,
} from "firebase/firestore";
import { AuthContext } from "../AuthContext";
import { normalizeRole } from "../utils/roleUtils";
import { isAdminUser } from "../config/admins";
import { useTranslation } from "../i18n/LanguageContext";
import {
  MOOD_OPTIONS,
  GENRE_GROUPS,
  GENRE_OPTIONS,
  EVENT_STATUS,
  resolveList,
  resolveStatus,
  toggleInList,
} from "../config/eventOptions";
import { ENABLE_TICKET_LINKS } from "../config/features";
import {
  fetchEventSuggestions,
  buildEventDraftFromSuggestion,
  confidenceLabel,
} from "../services/eventAssistant";
import FilterBar from "../components/filters/FilterBar";
import MultiSelect from "../components/filters/MultiSelect";
import SearchableSelect from "../components/filters/SearchableSelect";
import ScopeTabs from "../components/ScopeTabs";
import Drawer from "../components/Drawer";
import ContentCard from "../components/ContentCard";
import ImageUploadField from "../components/ImageUploadField";
import { getEventFallbackImage } from "../utils/eventImages";
import { formatEventDate, formatTimestampDate } from "../utils/formatDate";
import { parseSmartSearch } from "../utils/smartSearch";

// Bounded page size for each Firestore read channel (public/own/buddies/drafts).
const EVENTS_PAGE_SIZE = 20;

// In-memory (NOT sessionStorage) snapshot of the Events page's full browsing
// state, captured right before navigating into Event Details so "Back"
// (browser Back or the in-app "Back to Events" button) can restore the
// exact list the user was looking at: active tab, smart-search text, every
// filter, every already-loaded page of results — including Firestore
// pagination cursors, which are live SDK objects and can't be serialized
// into sessionStorage/JSON — and the scroll position. A plain module
// variable is deliberate: it naturally clears itself on a hard page reload
// (a fresh Events session should never resurrect a stale list from a
// previous visit) and is explicitly cleared by resetFilters() so
// intentionally starting a new search doesn't leave stale state around.
let eventsSnapshot = null;

function Events() {
  const [events, setEvents] = useState([]);
  const [publicEvents, setPublicEvents] = useState([]);
  const [buddyEvents, setBuddyEvents] = useState([]);
  const [ownEvents, setOwnEvents] = useState([]);
  const [draftEvents, setDraftEvents] = useState([]);
  const [userMap, setUserMap] = useState({});

  // Creation / edit / import form state
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [venue, setVenue] = useState("");
  const [date, setDate] = useState("");
  const [formGenres, setFormGenres] = useState([]);
  const [formMoods, setFormMoods] = useState([]);
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [formError, setFormError] = useState("");
  const [ticketUrl, setTicketUrl] = useState("");
  const [eventCoords, setEventCoords] = useState(null);
  const [visibility, setVisibility] = useState("buddies");
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Event Assistant (admin-only, mock) state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiSourceUrl, setAiSourceUrl] = useState("");
  const [aiCity, setAiCity] = useState("");
  const [aiGenres, setAiGenres] = useState([]);
  const [aiMoods, setAiMoods] = useState([]);
  const [aiStart, setAiStart] = useState("");
  const [aiEnd, setAiEnd] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);

  // Filter state
  const [filterCity, setFilterCity] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [radiusKm, setRadiusKm] = useState(5);
  const [userCoords, setUserCoords] = useState(null);
  const [useLocationFilter, setUseLocationFilter] = useState(false);

  // Smart search — the free-text field next to the Filters button. It never
  // maintains its own separate matching logic: recognizable parts of the
  // query are mapped straight into the SAME filter state above, and anything
  // left over becomes `searchFreeText`, matched client-side against
  // name/venue/description/city/country (see the filtering effect below).
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFreeText, setSearchFreeText] = useState("");
  const [tabCounts, setTabCounts] = useState({});

  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem("partybuddy.events.activeTab") || "public"
  );
  const [role, setRole] = useState(null);
  const [buddyIds, setBuddyIds] = useState([]);
  const [eventsError, setEventsError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [isLoadingMoreEvents, setIsLoadingMoreEvents] = useState(false);
  const eventsListRef = useRef([]);
  const userMapRef = useRef({});
  const eventCursorsRef = useRef({});
  const eventChannelsDoneRef = useRef({});
  // When a Back-navigation restore just happened, the very next run of the
  // load-events effect (triggered because `user`/`isAdmin`/`buddyIds` settle
  // asynchronously after mount, across SEVERAL separate renders — AuthContext's
  // own profile fetch AND this component's own buddyIds fetch resolve at
  // different times) must be skipped — otherwise any one of those settling
  // renders would fire a hard reset and overwrite the restored list. A single
  // one-shot boolean isn't enough for that (see skipResetUntilRef below).
  const skipNextResetRef = useRef(false);
  const skipNextSearchParseRef = useRef(false);
  const restoredScrollRef = useRef(null);
  // Epoch-ms deadline: while Date.now() is before this, auth-driven reruns of
  // the load-events effect are treated as "still settling" post-restore and
  // skipped, instead of only skipping the very first one. A genuine
  // user-triggered reload (Retry button -> reloadToken) always still fires
  // immediately, regardless of this window.
  const skipResetUntilRef = useRef(0);
  const prevReloadTokenRef = useRef(0);
  const { user, isAdmin } = useContext(AuthContext);
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  // Restore the previous Events browsing session (see `eventsSnapshot`
  // above) exactly once, right on mount — before anything else fetches or
  // resets state. Runs unconditionally; it's a no-op when there's no
  // snapshot (fresh visit / hard reload / user never opened an event).
  useEffect(() => {
    if (!eventsSnapshot) return;
    const snap = eventsSnapshot;
    eventsSnapshot = null;

    setActiveTab(snap.activeTab);
    setFilterCity(snap.filterCity);
    setSelectedGenres(snap.selectedGenres);
    setSelectedMoods(snap.selectedMoods);
    setFilterStartDate(snap.filterStartDate);
    setFilterEndDate(snap.filterEndDate);
    setUseLocationFilter(snap.useLocationFilter);
    setRadiusKm(snap.radiusKm);
    setSearchQuery(snap.searchQuery);
    setSearchFreeText(snap.searchFreeText);

    eventsListRef.current = snap.events;
    setEvents(snap.events);
    userMapRef.current = snap.userMap;
    setUserMap(snap.userMap);
    eventCursorsRef.current = snap.eventCursors;
    eventChannelsDoneRef.current = snap.eventChannelsDone;
    setHasMoreEvents(snap.hasMoreEvents);

    skipNextResetRef.current = true;
    skipNextSearchParseRef.current = true;
    skipResetUntilRef.current = Date.now() + 1500;
    restoredScrollRef.current = snap.scrollY;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Applies the restored scroll position once the restored list has actually
  // painted (a plain effect-time scrollTo would run before the grid has its
  // final height). A couple of animation frames is enough to wait past the
  // browser's own native scroll-restoration and this page's own render.
  useEffect(() => {
    if (restoredScrollRef.current == null) return;
    const targetY = restoredScrollRef.current;
    restoredScrollRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo(0, targetY));
    });
  }, [events]);

  // Remember the last-used Public/Buddies/Own/Drafts tab so "Back to Events"
  // from an Event Details page restores the same context instead of always
  // resetting to Public.
  useEffect(() => {
    sessionStorage.setItem("partybuddy.events.activeTab", activeTab);
  }, [activeTab]);

  const cityOptions = useMemo(
    () => [...new Set(events.map((e) => e.city).filter(Boolean))].sort(),
    [events]
  );
  const formatConfidence = useCallback(
    (value) => {
      const label = confidenceLabel(value);
      return t(`common.confidence.${label.toLowerCase()}`, label);
    },
    [t]
  );

  const deg2rad = useCallback((deg) => (deg * Math.PI) / 180, []);
  const getDistanceKm = useCallback(
    (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const dLat = deg2rad(lat2 - lat1);
      const dLon = deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    [deg2rad]
  );

  const requestUserLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => setUserCoords(null),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (useLocationFilter && !userCoords) {
      requestUserLocation();
    }
  }, [useLocationFilter, userCoords]);

  // Fetches one bounded page (EVENTS_PAGE_SIZE) per read channel (public/own/buddies/drafts)
  // and merges it into the existing event list. `isReset` starts every channel over from
  // scratch (used on identity/filter-relevant changes); otherwise each channel resumes from
  // its own Firestore cursor, so "Load more" never re-reads documents already fetched.
  //
  // Trade-off: none of these queries use orderBy(), because the existing composite indexes
  // (firestore.indexes.json) only cover the current equality filters — adding orderBy(date)
  // would require new composite indexes that aren't deployed yet, which would break these
  // reads in production. Firestore still supports limit()+startAfter() cursors without an
  // explicit orderBy (it falls back to ordering by document ID), so pagination here is real
  // and bounded, just not guaranteed to surface newest-first. See chat report for details.
  const loadEventsPage = useCallback(
    async (isReset) => {
      if (isReset) {
        eventCursorsRef.current = {};
        eventChannelsDoneRef.current = {};
        setEventsError("");
      }

      const eventsCol = collection(db, "events");

      // Each channel here must line up with a matching branch in firestore.rules'
      // /events read rule — Firestore rejects an entire list() if it could return
      // even one document the requester isn't allowed to see, so we can no longer
      // fetch the whole collection and filter client-side.
      const channels = [
        {
          key: "public",
          baseQuery: () =>
            query(eventsCol, where("status", "==", EVENT_STATUS.APPROVED), where("visibility", "==", "public")),
        },
      ];
      if (user) {
        channels.push({
          key: "own",
          baseQuery: () => query(eventsCol, where("creatorId", "==", user.uid)),
        });
        if (buddyIds.length > 0) {
          channels.push({
            key: "buddies",
            // Firestore's "in" operator accepts at most 10 values — with more than
            // 10 buddies, older buddies' buddy-only events won't appear here yet.
            baseQuery: () =>
              query(
                eventsCol,
                where("status", "==", EVENT_STATUS.APPROVED),
                where("visibility", "==", "buddies"),
                where("creatorId", "in", buddyIds.slice(0, 10))
              ),
          });
        }
      }
      if (isAdmin) {
        channels.push({
          key: "drafts",
          baseQuery: () => query(eventsCol, where("status", "==", EVENT_STATUS.DRAFT)),
        });
      }

      const activeChannels = channels.filter((c) => !eventChannelsDoneRef.current[c.key]);
      if (activeChannels.length === 0) {
        setHasMoreEvents(false);
        return;
      }

      try {
        const snaps = await Promise.all(
          activeChannels.map((c) => {
            const cursor = eventCursorsRef.current[c.key];
            const pageQuery = cursor
              ? query(c.baseQuery(), startAfter(cursor), limit(EVENTS_PAGE_SIZE))
              : query(c.baseQuery(), limit(EVENTS_PAGE_SIZE));
            return getDocs(pageQuery);
          })
        );

        const byId = new Map((isReset ? [] : eventsListRef.current).map((e) => [e.id, e]));
        activeChannels.forEach((c, idx) => {
          const snap = snaps[idx];
          snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
          if (snap.docs.length > 0) {
            eventCursorsRef.current[c.key] = snap.docs[snap.docs.length - 1];
          }
          if (snap.docs.length < EVENTS_PAGE_SIZE) {
            eventChannelsDoneRef.current[c.key] = true;
          }
        });

        const all = [...byId.values()];
        eventsListRef.current = all;
        setEvents(all);
        setHasMoreEvents(channels.some((c) => !eventChannelsDoneRef.current[c.key]));

        const ids = [...new Set(all.map((e) => e.creatorId))];
        const missingIds = ids.filter((id) => !userMapRef.current[id]);
        if (missingIds.length > 0) {
          const newUsers = {};
          for (const id of missingIds) {
            const uDoc = await getDoc(doc(db, "users", id));
            if (uDoc.exists()) newUsers[id] = uDoc.data();
          }
          userMapRef.current = { ...userMapRef.current, ...newUsers };
          setUserMap(userMapRef.current);
        }
      } catch (err) {
        console.error("Failed to load events:", err);
        setEventsError(t("events.loadError", "Couldn't load events right now. Please try again."));
      }
    },
    [user, isAdmin, buddyIds, t]
  );

  useEffect(() => {
    // See restore effect above: right after a Back-navigation restore,
    // user/isAdmin/buddyIds settle across several separate renders as
    // AuthContext's profile fetch and this component's own buddyIds fetch
    // each resolve — every one of those must be skipped, not just the
    // first, or the restored list gets clobbered by whichever one arrives
    // last. A genuine user-triggered reload (Retry -> reloadToken) always
    // bypasses this window immediately.
    const reloadChanged = prevReloadTokenRef.current !== reloadToken;
    prevReloadTokenRef.current = reloadToken;

    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      if (!reloadChanged) return;
    } else if (!reloadChanged && skipResetUntilRef.current && Date.now() < skipResetUntilRef.current) {
      return;
    }
    skipResetUntilRef.current = 0;
    loadEventsPage(true);
  }, [user, isAdmin, buddyIds, reloadToken, loadEventsPage]);

  const handleLoadMoreEvents = async () => {
    if (isLoadingMoreEvents || !hasMoreEvents) return;
    setIsLoadingMoreEvents(true);
    try {
      await loadEventsPage(false);
    } finally {
      setIsLoadingMoreEvents(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (curr) => {
      if (curr) {
        const docSnap = await getDoc(doc(db, "users", curr.uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRole(normalizeRole(data.role));
          setBuddyIds(data.friends || []);
        }
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!role && !isAdmin) return;
    const normalizedRole = normalizeRole(role);
    const canPublic = normalizedRole === "artist" || normalizedRole === "organizer" || isAdmin;
    setVisibility(canPublic ? "public" : "buddies");
  }, [role, isAdmin]);

  const resetFilters = () => {
    setFilterCity("");
    setSelectedGenres([]);
    setSelectedMoods([]);
    setFilterStartDate("");
    setFilterEndDate("");
    setUseLocationFilter(false);
    setRadiusKm(5);
    setSearchQuery("");
    setSearchFreeText("");
    // An explicit reset is exactly the "user intentionally starts a new
    // Events session" case — don't let a stale Back-restore snapshot
    // reappear after this.
    eventsSnapshot = null;
  };

  // Smart search: deterministically parse the query into the SAME filter
  // state the manual Filters panel edits (never a second/parallel filter
  // system) — see src/utils/smartSearch.js. A short debounce keeps typing
  // smooth while still feeling immediate. Recognized parts open in the
  // regular Filters panel exactly like a manual selection would.
  useEffect(() => {
    if (skipNextSearchParseRef.current) {
      // The just-restored searchQuery already has its matching filters
      // restored verbatim (which may include manual tweaks made after the
      // original search) — don't re-derive and clobber them on mount.
      skipNextSearchParseRef.current = false;
      return;
    }
    const handle = setTimeout(() => {
      const parsed = parseSmartSearch(searchQuery, {
        genreOptions: GENRE_OPTIONS,
        moodOptions: MOOD_OPTIONS,
        cityOptions,
      });
      if (parsed.city) setFilterCity(parsed.city);
      if (parsed.genres.length > 0) setSelectedGenres(parsed.genres);
      if (parsed.moods.length > 0) setSelectedMoods(parsed.moods);
      if (parsed.startDate) setFilterStartDate(parsed.startDate);
      if (parsed.endDate) setFilterEndDate(parsed.endDate);
      setSearchFreeText(parsed.freeText);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const getVisibility = useCallback(
    (event) => {
      const creator = userMap[event.creatorId];
      if (event.visibility) return event.visibility;
      return creator && (creator.role === "artist" || creator.role === "organizer")
        ? "public"
        : "buddies";
    },
    [userMap]
  );

  useEffect(() => {
    const freeTextLower = searchFreeText.trim().toLowerCase();
    const filtered = events.filter((e) => {
      const eventGenres = resolveList(e.genres, e.genre);
      const eventMoods = resolveList(e.moods, e.mood);

      const matchesCity = !filterCity || (e.city || "").toLowerCase().includes(filterCity.toLowerCase());
      const matchesGenre =
        selectedGenres.length === 0 || eventGenres.some((g) => selectedGenres.includes(g));
      const matchesMood = selectedMoods.length === 0 || eventMoods.some((m) => selectedMoods.includes(m));
      const matchesDateStart = !filterStartDate || e.date >= filterStartDate;
      const matchesDateEnd = !filterEndDate || e.date <= filterEndDate;
      const matchesLocation =
        !useLocationFilter || !userCoords || !e.location
          ? !useLocationFilter
          : getDistanceKm(userCoords.lat, userCoords.lng, e.location.lat, e.location.lng) <= radiusKm;
      // Smart-search leftover text that couldn't be mapped to a filter field
      // (see src/utils/smartSearch.js) — matched against every field the
      // spec calls out: name, venue, description, city, country.
      const matchesFreeText =
        !freeTextLower ||
        [e.name, e.venue, e.description, e.city, e.country]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(freeTextLower));

      return (
        matchesCity && matchesGenre && matchesMood && matchesDateStart && matchesDateEnd && matchesLocation && matchesFreeText
      );
    });

    // Only approved events are ever shown in the public / buddy / own feeds.
    const approved = filtered.filter((e) => resolveStatus(e) === EVENT_STATUS.APPROVED);

    // Public / Buddies / Yours are views over the same events, not separate
    // collections. An event appears in Public or Buddies by its visibility,
    // including the current user's own events. "Yours" is a personal management
    // view of everything the current user created.
    const ownList = approved.filter((e) => e.creatorId === user?.uid);
    const publicList = approved.filter((e) => getVisibility(e) === "public");
    const buddiesList = approved.filter(
      (e) =>
        getVisibility(e) === "buddies" &&
        (e.creatorId === user?.uid || buddyIds.includes(e.creatorId))
    );

    // Drafts awaiting review (admin-only tab). Rejected drafts stay hidden.
    const draftList = filtered.filter((e) => resolveStatus(e) === EVENT_STATUS.DRAFT);

    setOwnEvents(ownList);
    setPublicEvents(publicList);
    setBuddyEvents(buddiesList);
    setDraftEvents(draftList);
  }, [
    events,
    filterCity,
    selectedGenres,
    selectedMoods,
    filterStartDate,
    filterEndDate,
    searchFreeText,
    buddyIds,
    userMap,
    user,
    useLocationFilter,
    radiusKm,
    userCoords,
    getDistanceKm,
    getVisibility,
  ]);

  // ---- True event counter (requirement: total published count, not just
  // loaded/paginated items) ----
  //
  // Firestore has no substring or full-text query, so `filterCity`'s
  // case-insensitive substring match and the smart-search free-text match
  // can't be expressed as a server-side query — pushing them down would
  // require either a third-party search index (Algolia/Typesense) or
  // fetching every candidate document just to filter it locally, which the
  // spec explicitly rules out ("do not fetch all matching documents just to
  // count them"). Every filter that IS a plain equality/array field (city
  // chosen from the known dropdown, genres, moods, date range) is instead
  // pushed into a real `getCountFromServer()` aggregation query per read
  // channel — the same channels/security boundary as the paginated fetch
  // above — so counts never include anything the current user isn't
  // actually allowed to see.
  //
  // Trade-off (documented per the spec): whenever free-text/location-radius
  // search is active (the two predicates that truly can't be pushed to
  // Firestore), the counter falls back to the already-loaded, fully
  // client-filtered result count for that tab. If that tab's channels are
  // NOT fully paginated yet, the count is shown as a lower bound ("42+")
  // rather than silently presenting a partial number as if it were exact.
  const countChannelsForTab = useCallback(
    (tabKey) => {
      const eventsCol = collection(db, "events");
      const extra = [];
      // filterCity is always chosen from SearchableSelect's known-city list
      // (never freeform typing), so an exact equality match here is
      // equivalent to the substring match used for client-side filtering.
      if (filterCity) extra.push(where("city", "==", filterCity));
      if (filterStartDate) extra.push(where("date", ">=", filterStartDate));
      if (filterEndDate) extra.push(where("date", "<=", filterEndDate));
      // "in"/"array-contains-any" cap at 10 values — matches the same
      // Firestore limitation already documented for the buddies channel.
      if (selectedGenres.length > 0) extra.push(where("genres", "array-contains-any", selectedGenres.slice(0, 10)));
      if (selectedMoods.length > 0) extra.push(where("moods", "array-contains-any", selectedMoods.slice(0, 10)));

      const build = (...conds) => query(eventsCol, ...conds, ...extra);

      if (tabKey === "public") {
        return [build(where("status", "==", EVENT_STATUS.APPROVED), where("visibility", "==", "public"))];
      }
      if (tabKey === "buddies") {
        const qs = [];
        if (user) {
          qs.push(build(where("status", "==", EVENT_STATUS.APPROVED), where("visibility", "==", "buddies"), where("creatorId", "==", user.uid)));
        }
        if (buddyIds.length > 0) {
          qs.push(
            build(
              where("status", "==", EVENT_STATUS.APPROVED),
              where("visibility", "==", "buddies"),
              where("creatorId", "in", buddyIds.slice(0, 10))
            )
          );
        }
        return qs;
      }
      if (tabKey === "yours") {
        if (!user) return [];
        return [build(where("status", "==", EVENT_STATUS.APPROVED), where("creatorId", "==", user.uid))];
      }
      if (tabKey === "drafts") {
        if (!isAdmin) return [];
        return [build(where("status", "==", EVENT_STATUS.DRAFT))];
      }
      return [];
    },
    [user, isAdmin, buddyIds, filterCity, filterStartDate, filterEndDate, selectedGenres, selectedMoods]
  );

  const listForTabKey = useCallback(
    (tabKey) =>
      tabKey === "public"
        ? publicEvents
        : tabKey === "buddies"
        ? buddyEvents
        : tabKey === "yours"
        ? ownEvents
        : draftEvents,
    [publicEvents, buddyEvents, ownEvents, draftEvents]
  );

  const channelsDoneForTab = useCallback(
    (tabKey) => {
      if (tabKey === "drafts") return Boolean(eventChannelsDoneRef.current.drafts);
      if (tabKey === "yours") return Boolean(eventChannelsDoneRef.current.own);
      if (tabKey === "buddies") {
        return Boolean(eventChannelsDoneRef.current.own) && (buddyIds.length === 0 || Boolean(eventChannelsDoneRef.current.buddies));
      }
      return Boolean(eventChannelsDoneRef.current.public);
    },
    [buddyIds]
  );

  useEffect(() => {
    const unsupportedActive = Boolean(searchFreeText.trim()) || useLocationFilter;
    let cancelled = false;

    (async () => {
      const tabKeys = ["public", "buddies", "yours", ...(isAdmin ? ["drafts"] : [])];
      const nextCounts = {};

      await Promise.all(
        tabKeys.map(async (tabKey) => {
          if (unsupportedActive) {
            nextCounts[tabKey] = { count: listForTabKey(tabKey).length, approx: !channelsDoneForTab(tabKey) };
            return;
          }
          const countQueries = countChannelsForTab(tabKey);
          if (countQueries.length === 0) {
            nextCounts[tabKey] = { count: 0, approx: false };
            return;
          }
          try {
            const snaps = await Promise.all(countQueries.map((q) => getCountFromServer(q)));
            nextCounts[tabKey] = { count: snaps.reduce((sum, s) => sum + s.data().count, 0), approx: false };
          } catch (err) {
            // Most likely a missing Firestore composite index for this
            // exact filter combination (e.g. status+visibility+genres) —
            // fall back rather than break the page; see trade-off note above.
            console.error(`[Events] count query failed for tab "${tabKey}", falling back to loaded count`, err);
            nextCounts[tabKey] = { count: listForTabKey(tabKey).length, approx: !channelsDoneForTab(tabKey) };
          }
        })
      );

      if (!cancelled) setTabCounts(nextCounts);
    })();

    return () => {
      cancelled = true;
    };
  }, [countChannelsForTab, searchFreeText, useLocationFilter, isAdmin, listForTabKey, channelsDoneForTab]);

  const handleSetEventLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setEventCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => setEventCoords(null),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
  };

  const canPublish = () =>
    normalizeRole(role) === "artist" || normalizeRole(role) === "organizer" || isAdmin;

  const resetForm = () => {
    setEditMode(false);
    setEditingId(null);
    setName("");
    setCity("");
    setVenue("");
    setDate("");
    setFormGenres([]);
    setFormMoods([]);
    setDescription("");
    setImageUrl("");
    setImagePath("");
    setSourceUrl("");
    setTicketUrl("");
    setVisibility(canPublish() ? "public" : "buddies");
    setEventCoords(null);
    setFormError("");
    setShowForm(false);
  };

  // Cover image upload/replace/remove (progress, validation, Storage upload,
  // old-file cleanup) is fully owned by ImageUploadField — this just receives
  // the resulting {url, path} once it's done.
  const handleCoverChange = ({ url, path }) => {
    setImageUrl(url);
    setImagePath(path);
  };

  const openManualForm = () => {
    resetForm();
    setShowForm(true);
  };

  // ---- Event Assistant (admin-only, mock) ----
  const openAi = () => {
    setAiText("");
    setAiSourceUrl("");
    setAiCity(filterCity || "");
    setAiGenres([]);
    setAiMoods([]);
    setAiStart("");
    setAiEnd("");
    setAiResults([]);
    setAiLoading(false);
    setAiOpen(true);
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await fetchEventSuggestions({
        text: aiText,
        sourceUrl: aiSourceUrl,
        city: aiCity,
        genres: aiGenres,
        moods: aiMoods,
        startDate: aiStart,
        endDate: aiEnd,
      });
      setAiResults(res);
    } finally {
      setAiLoading(false);
    }
  };

  const saveSuggestionAsDraft = async (suggestion) => {
    if (!auth.currentUser) return;
    const draft = buildEventDraftFromSuggestion(suggestion, { adminUid: auth.currentUser.uid });
    const refDoc = await addDoc(collection(db, "events"), { ...draft, createdAt: serverTimestamp() });
    setAiResults((prev) => prev.filter((s) => s !== suggestion));
    // Reflect the new draft locally so it appears in the admin Drafts tab.
    setEvents((prev) => [...prev, { id: refDoc.id, ...draft }]);
  };

  const saveAllAsDrafts = async () => {
    if (!auth.currentUser || aiResults.length === 0) return;
    await Promise.all(
      aiResults.map((s) =>
        addDoc(collection(db, "events"), {
          ...buildEventDraftFromSuggestion(s, { adminUid: auth.currentUser.uid }),
          createdAt: serverTimestamp(),
        })
      )
    );
    setAiResults([]);
    window.location.reload();
  };

  const buildEventData = () => {
    const data = {
      name,
      city,
      venue,
      date,
      genres: formGenres,
      moods: formMoods,
      description,
      visibility: canPublish() ? visibility : "buddies",
      sourceUrl: sourceUrl || "",
    };
    if (imageUrl) data.imageUrl = imageUrl;
    if (imagePath) data.imagePath = imagePath;
    if (eventCoords) data.location = eventCoords;
    // Ticket links are a locked premium feature — only saved when enabled.
    if (ENABLE_TICKET_LINKS && ticketUrl) data.ticketUrl = ticketUrl;
    return data;
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!name || !city || !date || formGenres.length === 0) return;

    setFormError("");
    try {
      // Manual creation publishes directly (approved/public).
      const eventData = {
        ...buildEventData(),
        status: EVENT_STATUS.APPROVED,
        importedBy: "",
        creatorId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "events"), eventData);

      resetForm();
      window.location.reload();
    } catch (err) {
      setFormError(t("events.saveError", "Could not save the event. Please try again."));
    }
  };

  const startEdit = (event) => {
    setEditMode(true);
    setEditingId(event.id);
    setName(event.name || "");
    setCity(event.city || "");
    setVenue(event.venue || "");
    setDate(event.date || "");
    setFormGenres(resolveList(event.genres, event.genre));
    setFormMoods(resolveList(event.moods, event.mood));
    setDescription(event.description || "");
    setImageUrl(event.imageUrl || "");
    setImagePath(event.imagePath || "");
    setSourceUrl(event.sourceUrl || "");
    setTicketUrl(event.ticketUrl || "");
    setVisibility(event.visibility || (canPublish() ? "public" : "buddies"));
    setEventCoords(event.location || null);
    setShowForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingId) return;

    setFormError("");
    try {
      // Editing leaves the moderation status untouched (a draft stays a draft).
      const updatedData = {
        ...buildEventData(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "events", editingId), updatedData);

      resetForm();
      window.location.reload();
    } catch (err) {
      setFormError(t("events.saveError", "Could not save the event. Please try again."));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("events.confirmDelete", "Do you really want to delete this event?"))) return;
    await deleteDoc(doc(db, "events", id));
    setEvents((prev) => prev.filter((e) => e.id !== id));
  };

  // Duplicate: pre-fill the manual creation drawer with a copy of the event so
  // the user can tweak it and save it as a new event.
  const handleDuplicate = (event) => {
    resetForm();
    setName(event.name ? `${event.name} ${t("events.copySuffix", "(Copy)")}` : "");
    setCity(event.city || "");
    setVenue(event.venue || "");
    setDate(event.date || "");
    setFormGenres(resolveList(event.genres, event.genre));
    setFormMoods(resolveList(event.moods, event.mood));
    setDescription(event.description || "");
    setImageUrl(event.imageUrl || "");
    // Not carried over: the duplicate must never delete the original event's
    // Storage file if its image is later replaced or removed.
    setImagePath("");
    setSourceUrl(event.sourceUrl || "");
    setTicketUrl(event.ticketUrl || "");
    setVisibility(event.visibility || (canPublish() ? "public" : "buddies"));
    setEventCoords(event.location || null);
    setShowForm(true);
  };

  const handleApprove = async (event) => {
    // Approved events become public immediately.
    await updateDoc(doc(db, "events", event.id), {
      status: EVENT_STATUS.APPROVED,
      visibility: "public",
      approvedBy: auth.currentUser.uid,
      approvedAt: serverTimestamp(),
    });
    setEvents((prev) =>
      prev.map((e) =>
        e.id === event.id ? { ...e, status: EVENT_STATUS.APPROVED, visibility: "public" } : e
      )
    );
  };

  const handleReject = async (event) => {
    // Rejected drafts remain hidden everywhere (kept in Firestore for audit).
    await updateDoc(doc(db, "events", event.id), { status: EVENT_STATUS.REJECTED });
    setEvents((prev) =>
      prev.map((e) => (e.id === event.id ? { ...e, status: EVENT_STATUS.REJECTED } : e))
    );
  };

  // Active filter tags shown in the collapsed bar (each removable individually).
  const activeTags = [];
  if (filterCity) activeTags.push({ key: "city", label: filterCity, onRemove: () => setFilterCity("") });
  selectedGenres.forEach((g) =>
    activeTags.push({ key: `g-${g}`, label: g, onRemove: () => setSelectedGenres((p) => p.filter((x) => x !== g)) })
  );
  selectedMoods.forEach((m) =>
    activeTags.push({ key: `m-${m}`, label: m, onRemove: () => setSelectedMoods((p) => p.filter((x) => x !== m)) })
  );
  if (filterStartDate || filterEndDate) {
    activeTags.push({
      key: "dates",
      label: `${filterStartDate || "…"} – ${filterEndDate || "…"}`,
      onRemove: () => {
        setFilterStartDate("");
        setFilterEndDate("");
      },
    });
  }
  if (useLocationFilter) {
    activeTags.push({ key: "loc", label: `≤${radiusKm}km`, onRemove: () => setUseLocationFilter(false) });
  }
  if (searchQuery.trim()) {
    activeTags.unshift({
      key: "search",
      label: `⌕ ${searchQuery.trim()}`,
      onRemove: () => {
        setSearchQuery("");
        setSearchFreeText("");
      },
    });
  }

  const canCreate = ["artist", "organizer", "partybuddy"].includes(normalizeRole(role));
  const canChooseVisibility = canPublish();

  // Formats a tab/header count, appending "+" for the documented
  // lower-bound fallback (see the counter effect above) so it's never
  // presented as an exact number when it isn't one.
  const formatCount = (tabKey, fallbackLength) => {
    const entry = tabCounts[tabKey];
    if (!entry) return fallbackLength;
    return entry.approx ? `${entry.count}+` : entry.count;
  };
  const isCountApprox = (tabKey) => Boolean(tabCounts[tabKey]?.approx);

  const tabs = [
    { key: "public", label: t("events.tabPublic", "Public"), count: formatCount("public", publicEvents.length) },
    { key: "buddies", label: t("events.tabBuddies", "Buddies"), count: formatCount("buddies", buddyEvents.length) },
    { key: "yours", label: t("events.tabOwn", "Own"), count: formatCount("yours", ownEvents.length) },
    ...(isAdmin
      ? [{ key: "drafts", label: t("events.tabDrafts", "Drafts"), count: formatCount("drafts", draftEvents.length) }]
      : []),
  ];
  const safeTab = activeTab === "drafts" && !isAdmin ? "public" : activeTab;
  const activeList =
    safeTab === "public"
      ? publicEvents
      : safeTab === "buddies"
      ? buddyEvents
      : safeTab === "drafts"
      ? draftEvents
      : ownEvents;
  const activeCount = formatCount(safeTab, activeList.length);

  const openEventDetails = (event) => {
    // Capture the exact browsing state (filters, smart search, every loaded
    // page, and scroll position) so "Back" from Event Details restores this
    // list precisely instead of a fresh Events render (requirement 11).
    eventsSnapshot = {
      activeTab: safeTab,
      filterCity,
      selectedGenres,
      selectedMoods,
      filterStartDate,
      filterEndDate,
      useLocationFilter,
      radiusKm,
      searchQuery,
      searchFreeText,
      events: eventsListRef.current,
      userMap: userMapRef.current,
      eventCursors: eventCursorsRef.current,
      eventChannelsDone: eventChannelsDoneRef.current,
      hasMoreEvents,
      scrollY: window.scrollY,
    };
    navigate(`/event/${event.id}`);
  };

  const renderCard = (event, index = 0) => {
    const creator = userMap[event.creatorId];
    const genres = resolveList(event.genres, event.genre);
    const moods = resolveList(event.moods, event.mood);
    const status = resolveStatus(event);
    const isDraftTab = safeTab === "drafts";
    const isAi = event.source === "ai_suggestion";
    const subtitleParts = [event.venue, event.city].filter(Boolean).join(" · ");
    const badges = [
      ...(status !== EVENT_STATUS.APPROVED ? [{ label: t(`events.status.${status}`, status.toUpperCase()), tone: "draft" }] : []),
      ...(isDraftTab && isAi ? [{ label: "AI", tone: "ai" }] : []),
      ...genres.map((g) => ({ label: g, tone: "genre" })),
      ...moods.map((m) => ({ label: m, tone: "mood" })),
    ];

    let actions = null;
    if (isDraftTab) {
      actions = (
        <>
          {event.sourceUrl && (
            <a
              href={event.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-sky-300 transition hover:text-sky-200"
            >
              {t("events.source", "Source")}
            </a>
          )}
          <button
            onClick={() => startEdit(event)}
            className="text-xs font-semibold text-yellow-400 transition hover:text-yellow-300"
          >
            {t("events.edit", "Edit")}
          </button>
          <button
            onClick={() => handleReject(event)}
            className="text-xs font-semibold text-red-400 transition hover:text-red-300"
          >
            {t("events.reject", "Reject")}
          </button>
          <button
            onClick={() => handleApprove(event)}
            className="text-xs font-semibold text-emerald-400 transition hover:text-emerald-300"
          >
            {t("events.approve", "Approve")}
          </button>
        </>
      );
    } else if (safeTab === "yours" && event.creatorId === user?.uid) {
      actions = (
        <>
          <button
            onClick={() => startEdit(event)}
            className="text-xs font-semibold text-yellow-400 transition hover:text-yellow-300"
          >
            {t("events.edit", "Edit")}
          </button>
          <button
            onClick={() => handleDuplicate(event)}
            className="text-xs font-semibold text-sky-300 transition hover:text-sky-200"
          >
            {t("events.duplicate", "Duplicate")}
          </button>
          <button
            onClick={() => handleDelete(event.id)}
            className="text-xs font-semibold text-red-400 transition hover:text-red-300"
          >
            {t("events.delete", "Delete")}
          </button>
        </>
      );
    }

    return (
      <ContentCard
        key={event.id}
        onCardClick={() => openEventDetails(event)}
        imageUrl={event.imageUrl}
        fallbackImageUrl={getEventFallbackImage({ id: event.id, genres, moods })}
        badges={badges}
        title={event.name}
        subtitle={
          isDraftTab && isAi
            ? `${t("events.confidence", "Confidence")}: ${formatConfidence(event.confidence)}`
            : `${subtitleParts} · ${formatEventDate(event.date, language)}`
        }
        excerpt={isDraftTab && event.notes ? `${event.description || ""}\n\n${event.notes}` : event.description}
        authorName={isAi || isAdminUser(creator) ? "" : creator?.username || ""}
        authorId={event.creatorId}
        authorPhotoUrl={isAi || isAdminUser(creator) ? "" : creator?.photoUrl || ""}
        authorAvatarConfig={isAi || isAdminUser(creator) ? null : creator?.avatarConfig || null}
        postedLabel={formatTimestampDate(event.createdAt, language) ? `${t("events.postedOn", "Posted")} ${formatTimestampDate(event.createdAt, language)}` : ""}
        ticketUrl={event.ticketUrl}
        ticketLabel={t("ticket.buy", "Buy tickets")}
        noImageLabel={t("event.noImage", "No image")}
        authorPrefix={t("events.by", "By")}
        actions={actions}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 p-4 md:p-6 text-sm">
      {/* Header + collapsible filter bar */}
      <div className="mx-auto mb-4 flex max-w-7xl flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-white">
          {t("events.title", "Events")}{" "}
          <span
            className="text-slate-400"
            title={
              isCountApprox(safeTab)
                ? t(
                    "events.countApproxHint",
                    "Approximate: based on results loaded so far (free-text/location search can't be counted exactly server-side)"
                  )
                : t("events.countExactHint", "Exact total matching count from the server")
            }
          >
            · {activeCount}
          </span>
        </h1>
        {/* Smart search — compact free-text field next to Filters. Recognized
            parts (city/date/genre/mood) are written straight into the same
            filter state used by the panel below; anything left over is used
            as free text (see src/utils/smartSearch.js). */}
        <div className="relative flex items-center">
          <span className="pointer-events-none absolute left-3 text-slate-400" aria-hidden>⌕</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search.placeholder", "Try \u201cTechno Graz tomorrow\u201d")}
            aria-label={t("search.label", "Smart search")}
            className="w-44 rounded-full border border-slate-600 bg-slate-800/90 py-1.5 pl-8 pr-7 text-xs text-white placeholder-slate-500 outline-none transition focus:w-64 focus:border-sky-400 sm:w-56 sm:focus:w-72"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setSearchFreeText("");
              }}
              aria-label={t("search.clear", "Clear search")}
              className="absolute right-2 text-slate-400 transition hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
        <FilterBar activeTags={activeTags} onClearAll={resetFilters}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.city", "City")}</span>
              <SearchableSelect
                value={filterCity}
                onChange={setFilterCity}
                options={cityOptions}
                placeholder={t("filters.city", "City")}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.from", "From")}</span>
                <input
                  type="date"
                  className="w-full rounded-2xl border border-slate-700 bg-slate-900/95 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.to", "To")}</span>
                <input
                  type="date"
                  className="w-full rounded-2xl border border-slate-700 bg-slate-900/95 px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </label>
            </div>
            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.mood", "Mood")}</span>
              <MultiSelect
                options={MOOD_OPTIONS}
                selected={selectedMoods}
                onToggle={(value) => setSelectedMoods((prev) => toggleInList(prev, value))}
              />
            </div>
            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.genre", "Genre")}</span>
              <MultiSelect
                groups={GENRE_GROUPS}
                selected={selectedGenres}
                onToggle={(value) => setSelectedGenres((prev) => toggleInList(prev, value))}
              />
            </div>
            <div className="rounded-2xl border border-slate-700/80 bg-slate-900/80 p-3 md:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-300">{t("filters.location", "Location")}</span>
                <button
                  type="button"
                  onClick={() => setUseLocationFilter((prev) => !prev)}
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                    useLocationFilter ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                  }`}
                >
                  {useLocationFilter ? t("common.on", "On") : t("common.off", "Off")}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <span>{t("filters.radius", "Radius")} {radiusKm} km</span>
                <span>{userCoords ? t("filters.locationReady", "Location ready") : t("filters.locationPending", "Location pending")}</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="mt-2 w-full accent-sky-400"
                disabled={!useLocationFilter}
              />
            </div>
          </div>
        </FilterBar>
        {canCreate && (
          <button
            onClick={openManualForm}
            className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-500 hover:to-indigo-500"
          >
            + {t("events.addEvent", "New event")}
          </button>
        )}
        {isAdmin && (
          <button
            onClick={openAi}
            className="rounded-2xl border border-violet-500/60 bg-violet-600/20 px-4 py-2 text-sm font-semibold text-violet-100 transition hover:bg-violet-600/40"
          >
            ✨ {t("events.assistant", "Event Assistant")}
          </button>
        )}
      </div>

      {/* Scope tabs */}
      <div className="mx-auto mb-4 max-w-7xl">
        <ScopeTabs tabs={tabs} active={safeTab} onChange={setActiveTab} />
      </div>

      {eventsError && (
        <div className="mx-auto mb-4 max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            <span>⚠️ {eventsError}</span>
            <button
              type="button"
              onClick={() => setReloadToken((n) => n + 1)}
              className="rounded-lg border border-red-400/60 bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-100 transition hover:bg-red-500/30"
            >
              {t("common.retry", "Retry")}
            </button>
          </div>
        </div>
      )}

      {/* Card grid */}
      <section className="mx-auto max-w-7xl">
        {activeList.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-800/70 p-6 text-center text-slate-300">
            {safeTab === "drafts" ? t("events.emptyDrafts", "No drafts awaiting review.") : t("events.emptyEvents", "No events here yet.")}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {activeList.map((event, index) => renderCard(event, index))}
          </div>
        )}

        {hasMoreEvents && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={handleLoadMoreEvents}
              disabled={isLoadingMoreEvents}
              className="rounded-2xl border border-slate-600 bg-slate-800/80 px-6 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoadingMoreEvents
                ? t("common.loadingMore", "Loading…")
                : t("common.loadMore", "Load more")}
            </button>
          </div>
        )}
      </section>

      {/* Manual creation / edit drawer */}
      <Drawer
        open={showForm}
        title={editMode ? t("events.drawerEditTitle", "Edit event") : t("events.drawerNewTitle", "New event")}
        onClose={resetForm}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-slate-600 bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
            >
              {t("events.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              form="event-form"
              className={`rounded-xl px-5 py-2 text-sm font-semibold text-white shadow-lg transition ${
                editMode
                  ? "bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500"
                  : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
              }`}
            >
              {editMode ? t("events.update", "Update") : t("events.save", "Save")}
            </button>
          </div>
        }
      >
        <form id="event-form" onSubmit={editMode ? handleUpdate : handleAddEvent} className="space-y-4">
          {formError && (
            <p className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-300">{formError}</p>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldName", "Name")}</span>
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("events.fieldName", "Name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.city", "City")}</span>
              <SearchableSelect value={city} onChange={setCity} options={cityOptions} placeholder={t("filters.city", "City")} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldDate", "Date")}</span>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white outline-none transition focus:ring-2 focus:ring-blue-400"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldVenue", "Venue / location")}</span>
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("events.venuePlaceholder", "Venue (optional)")}
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            />
          </label>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldMoods", "Moods")}</span>
            <MultiSelect
              options={MOOD_OPTIONS}
              selected={formMoods}
              onToggle={(value) => setFormMoods((prev) => toggleInList(prev, value))}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldGenres", "Genres")}</span>
            <MultiSelect
              groups={GENRE_GROUPS}
              selected={formGenres}
              onToggle={(value) => setFormGenres((prev) => toggleInList(prev, value))}
            />
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldDescription", "Description")}</span>
            <textarea
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("events.descriptionPlaceholder", "Description (optional)")}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <div className="space-y-2">
            <ImageUploadField
              value={imageUrl}
              currentPath={imagePath}
              storagePathPrefix="eventCovers"
              onChange={handleCoverChange}
              helperText={t("events.coverImageHelp", "A cover image is recommended — events with a cover stand out. (Optional)")}
            />
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
              placeholder={t("events.imageUrlPlaceholder", "…or paste an image URL")}
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setImagePath("");
              }}
            />
          </div>
          {sourceUrl && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.sourceUrlLabel", "Source URL")}</span>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
                placeholder={t("events.sourceUrlPlaceholder", "https://…")}
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </label>
          )}
          {/* Ticket link — locked premium feature (ENABLE_TICKET_LINKS). */}
          <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-300">🎟️ {t("ticket.add", "Add ticket link")}</span>
              {!ENABLE_TICKET_LINKS && (
                <span className="rounded-full border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                  {t("ticket.locked", "Premium feature – coming soon")}
                </span>
              )}
            </div>
            <input
              className="mt-2 w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="https://tickets…"
              value={ticketUrl}
              onChange={(e) => setTicketUrl(e.target.value)}
              disabled={!ENABLE_TICKET_LINKS}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.eventLocation", "Event location")}</span>
            <button
              type="button"
              onClick={handleSetEventLocation}
              className="w-full rounded-lg bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-600"
            >
              {t("events.useCurrentLocation", "Use current location")}
            </button>
            <span className="mt-1 block text-xs text-gray-400">
              {eventCoords
                ? `${t("events.locationSaved", "Location saved")} (${eventCoords.lat.toFixed(4)}, ${eventCoords.lng.toFixed(4)})`
                : t("events.noEventLocation", "No event location set")}
            </span>
          </div>
          {canChooseVisibility ? (
            <div>
              <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.visibility", "Visibility")}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVisibility("public")}
                  className={`flex-1 rounded-lg px-4 py-3 text-sm font-semibold transition ${
                    visibility === "public" ? "bg-blue-600 text-white" : "bg-slate-700 text-gray-300 hover:bg-slate-600"
                  }`}
                >
                  {t("events.public", "Public")}
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("buddies")}
                  className={`flex-1 rounded-lg px-4 py-3 text-sm font-semibold transition ${
                    visibility === "buddies" ? "bg-blue-600 text-white" : "bg-slate-700 text-gray-300 hover:bg-slate-600"
                  }`}
                >
                  {t("events.buddiesOnly", "Buddies only")}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400">{t("events.buddiesOnlyNotice", "PartyBuddy users can publish events for buddies only.")}</p>
          )}
        </form>
      </Drawer>

      {/* Event Assistant drawer (admin-only, mock) */}
      <Drawer
        open={aiOpen}
        title={t("events.assistant", "Event Assistant")}
        onClose={() => setAiOpen(false)}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              className="rounded-xl border border-slate-600 bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
            >
              {t("events.close", "Close")}
            </button>
          </div>
        }
      >
        <p className="rounded-xl border border-violet-500/40 bg-violet-600/10 p-3 text-[11px] text-violet-200">
          {t("events.mockNotice", "Mock mode — no internet search or AI is connected yet. Suggestions are saved as drafts only and must be approved before they appear publicly.")}
        </p>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.pasteAtoms", "Paste Atoms results (JSON) or text")}</span>
          <textarea
            className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
            placeholder={t("events.pasteAtomsPlaceholder", 'Paste Atoms JSON, e.g. [{ "name": "…", "city": "…", "date": "YYYY-MM-DD" }]')}
            rows={4}
            value={aiText}
            onChange={(e) => setAiText(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.sourceUrlLabel", "Source URL")}</span>
          <input
            className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-blue-400"
            placeholder={t("events.sourceUrlPlaceholder", "https://…")}
            value={aiSourceUrl}
            onChange={(e) => setAiSourceUrl(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.city", "City")}</span>
          <SearchableSelect value={aiCity} onChange={setAiCity} options={cityOptions} placeholder={t("filters.city", "City")} />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.from", "From")}</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white outline-none transition focus:ring-2 focus:ring-blue-400"
              value={aiStart}
              onChange={(e) => setAiStart(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-300">{t("filters.to", "To")}</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 p-3 text-white outline-none transition focus:ring-2 focus:ring-blue-400"
              value={aiEnd}
              onChange={(e) => setAiEnd(e.target.value)}
            />
          </label>
        </div>

        <div>
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldMoods", "Moods")}</span>
          <MultiSelect
            options={MOOD_OPTIONS}
            selected={aiMoods}
            onToggle={(value) => setAiMoods((prev) => toggleInList(prev, value))}
          />
        </div>
        <div>
          <span className="mb-1 block text-xs font-medium text-slate-300">{t("events.fieldGenres", "Genres")}</span>
          <MultiSelect
            groups={GENRE_GROUPS}
            selected={aiGenres}
            onToggle={(value) => setAiGenres((prev) => toggleInList(prev, value))}
          />
        </div>

        <button
          type="button"
          onClick={runAi}
          disabled={aiLoading}
          className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-violet-500 hover:to-indigo-500 disabled:opacity-60"
        >
          {aiLoading ? t("events.searching", "Searching…") : t("events.findSuggestions", "Find suggestions")}
        </button>

        {/* Results */}
        {!aiLoading && aiResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">{aiResults.length} {t("events.suggestions", "suggestions")}</span>
              <button
                type="button"
                onClick={saveAllAsDrafts}
                className="rounded-lg bg-emerald-600/90 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-500"
              >
                {t("events.saveAll", "Save all as drafts")}
              </button>
            </div>
            {aiResults.map((suggestion, index) => (
              <div key={index} className="space-y-1 rounded-xl border border-slate-700 bg-slate-900/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white">{suggestion.name}</h4>
                  <span className="shrink-0 rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-200">
                    {formatConfidence(suggestion.confidence)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  {[suggestion.city, ...(suggestion.genres || []), ...(suggestion.moods || [])].filter(Boolean).join(" · ")}
                </p>
                <p className="text-xs text-slate-300">{suggestion.description}</p>
                {suggestion.notes && <p className="text-[11px] text-amber-300/80">{suggestion.notes}</p>}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => saveSuggestionAsDraft(suggestion)}
                    className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-600"
                  >
                    {t("events.saveDraft", "Save as draft")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!aiLoading && aiResults.length === 0 && (
          <p className="text-xs text-slate-400">{t("events.noSuggestions", "No suggestions yet. Set your filters and run a search.")}</p>
        )}
      </Drawer>
    </div>
  );
}

export default Events;
